import * as Sentry from "@sentry/react";

const TOKEN_KEY = "token";

// "session" keeps the token per tab, so closing the tab or browser ends the session.
const useSessionStorage = process.env.REACT_APP_SESSION_TOKEN_STORAGE === "session";

// Builds that moved the token to sessionStorage leave a still-valid token from the
// previous build behind in localStorage, which nothing reads any more. Drop it.
if (useSessionStorage) {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    // Blocked storage has nothing to clear.
  }
}

// Set once this page has seen a token, so a later miss means the browser dropped it
// rather than the user never having signed in.
let hasSession = false;

// Held only while a recording is in progress, so the upload at the end of a recording
// still has a token if the browser clears storage part-way through it.
let pinnedToken: string | null = null;

let onLost: (() => void) | null = null;

/** Where the token lives. Throws when the browser blocks storage. */
export function tokenStorage(): Storage {
  return useSessionStorage ? sessionStorage : localStorage;
}

function readStoredToken(): string | null {
  try {
    return tokenStorage().getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function storageKeyNames(): string {
  try {
    return Object.keys(localStorage).sort().join(",");
  } catch {
    return "unreadable";
  }
}

function reportLoss(): void {
  hasSession = false;
  try {
    // Key names only, never values: they show whether the whole origin was cleared
    // or only some of our keys.
    Sentry.logger.warn("Failed to fetch token from local storage", {
      event: "browser_storage.token_retrieve_failure",
      remaining_keys: storageKeyNames(),
      recording_in_progress: pinnedToken !== null,
    });
  } catch {
    // Logging must never block ending the session.
  }
  onLost?.();
}

/** Called when a token this page relied on disappears from storage. */
export function setSessionTokenLostHandler(handler: (() => void) | null): void {
  onLost = handler;
}

/** Shown by the sign-in screens when the browser won't store the token. */
export const SESSION_STORAGE_BLOCKED_MESSAGE =
  "Your browser is blocking site storage, so Knowtex can't keep you signed in. " +
  "Allow storage for this site, or try another browser, and sign in again.";

/**
 * Stores the JWT. Returns false when the browser refuses the write; sign-in must not
 * complete then, because every request afterwards would go out without a token.
 */
export function setSessionToken(token: string): boolean {
  try {
    tokenStorage().setItem(TOKEN_KEY, token);
  } catch {
    try {
      Sentry.logger.warn("Failed to write token to local storage", {
        event: "browser_storage.token_write_failure",
      });
    } catch {
      // Logging must never change the outcome of a sign-in.
    }
    return false;
  }
  hasSession = true;
  return true;
}

/** Signs this page out. Not a loss, so it doesn't report one. */
export function clearSessionToken(): void {
  hasSession = false;
  pinnedToken = null;
  try {
    tokenStorage().removeItem(TOKEN_KEY);
  } catch {
    // Blocked storage has nothing to clear.
  }
}

/**
 * Removes `token` from storage and the recording pin if it's still the current one. A
 * different stored token belongs to a newer launch and stays.
 */
export function forgetSessionToken(token: string): void {
  if (pinnedToken === token) pinnedToken = null;
  if (readStoredToken() !== token) return;
  hasSession = false;
  try {
    tokenStorage().removeItem(TOKEN_KEY);
  } catch {
    // Blocked storage has nothing to clear.
  }
}

/** The current token without loss detection, for code deciding whether to end the session. */
export function peekSessionToken(): string | null {
  return readStoredToken() ?? pinnedToken;
}

/**
 * The token every authenticated request sends. Reports a loss the first time a token this
 * page had is missing; during a recording the pinned copy is returned instead.
 */
export function getSessionToken(): string | null {
  const stored = readStoredToken();
  if (stored) {
    hasSession = true;
    return stored;
  }
  if (hasSession) reportLoss();
  return pinnedToken;
}

export function pinSessionToken(): void {
  pinnedToken = readStoredToken();
}

export function unpinSessionToken(): void {
  pinnedToken = null;
}

/** Test-only. Leaves the lost-token handler, which sessionExpiry registers on import. */
export function resetSessionTokenState(): void {
  hasSession = false;
  pinnedToken = null;
}
